import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  Tooltip,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import { payrollApi } from '../../../api/payrollApi';
import {
  PayrollGroupAssignmentOptionsResponseDto,
  PayrollGroupGenerationResultDto,
} from '../../../types/payrollTypes';
import { useLanguage } from '../../../i18n/LocalizationContext';

const toDateInput = (value: Date) => value.toISOString().slice(0, 10);

const PayrollGroupAssignmentsPage: React.FC = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState<PayrollGroupAssignmentOptionsResponseDto | null>(null);
  const [result, setResult] = useState<PayrollGroupGenerationResultDto | null>(null);

  const [groupId, setGroupId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(toDateInput(new Date()));
  const [defaultBaseAmount, setDefaultBaseAmount] = useState<string>('');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [baseAmounts, setBaseAmounts] = useState<Record<string, string>>({});

  const loadOptions = async () => {
    setLoading(true);
    try {
      const data = await payrollApi.getGroupAssignmentOptions();
      setOptions(data);
      setGroupId((prev) => prev || data.groups[0]?.id || '');

      const nextSelected: Record<string, boolean> = {};
      const nextBase: Record<string, string> = {};
      data.employees.forEach((employee) => {
        nextSelected[employee.employeeId] = employee.payrollEnabled && employee.isActive;
        nextBase[employee.employeeId] = employee.assignedBaseAmount?.toString() ?? '';
      });

      setSelected(nextSelected);
      setBaseAmounts(nextBase);
    } catch {
      toast.error(t('messages.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadOptions();
  }, []);

  const employees = useMemo(() => {
    if (!options) return [];
    if (!departmentId) return options.employees;
    return options.employees.filter((employee) => employee.departmentId === departmentId);
  }, [options, departmentId]);

  const selectableEmployees = useMemo(
    () => employees.filter((employee) => employee.payrollEnabled && employee.isActive),
    [employees]
  );
  const selectedCount = selectableEmployees.filter((employee) => selected[employee.employeeId]).length;
  const allSelected = selectableEmployees.length > 0 && selectedCount === selectableEmployees.length;
  const selectedAssignments = useMemo(
    () => employees.filter((employee) => selected[employee.employeeId] && employee.assignedGroupId),
    [employees, selected]
  );

  const toggleAll = () => {
    setSelected((prev) => {
      const shouldSelect = !selectableEmployees.every((employee) => prev[employee.employeeId]);
      const next = { ...prev };

      selectableEmployees.forEach((employee) => {
        next[employee.employeeId] = shouldSelect;
      });

      return next;
    });
  };

  const handleApply = async () => {
    if (!groupId) {
      toast.error(t('payroll.selectGroup'));
      return;
    }

    const selectedEmployees = employees
      .filter((employee) => selected[employee.employeeId])
      .map((employee) => ({
        employeeId: employee.employeeId,
        baseAmount: baseAmounts[employee.employeeId] !== '' ? Number(baseAmounts[employee.employeeId]) : undefined,
      }));

    if (selectedEmployees.length === 0) {
      toast.error(t('payroll.selectEmployee'));
      return;
    }

    const defaultBase = defaultBaseAmount !== '' ? Number(defaultBaseAmount) : undefined;
    const invalid = selectedEmployees.some((employee) => {
      const amount = employee.baseAmount ?? defaultBase;
      return !amount || amount <= 0;
    });
    if (invalid) {
      toast.error(t('payroll.validBaseAmount'));
      return;
    }

    setSaving(true);
    try {
      const response = await payrollApi.applyPayrollGroup({
        groupId,
        effectiveFrom,
        departmentId: departmentId || undefined,
        defaultBaseAmount: defaultBase,
        employees: selectedEmployees,
      });
      setResult(response);
      toast.success(t('payroll.generatedStructures', { count: response.processedCount }));
      await loadOptions();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('payroll.applyFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveAssignment = async (employeeId: string, employeeName: string) => {
    if (!window.confirm(t('payroll.removeEmployeeConfirm', { employeeName }))) {
      return;
    }

    setSaving(true);
    try {
      await payrollApi.removePayrollGroupAssignment(employeeId);
      toast.success(t('payroll.removedOne'));
      await loadOptions();
      setSelected((prev) => ({ ...prev, [employeeId]: false }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message || t('payroll.removeFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleBulkRemoveAssignments = async () => {
    if (selectedAssignments.length < 2) {
      return;
    }

    const confirmed = window.confirm(
      t('payroll.bulkDeleteConfirm', { count: selectedAssignments.length })
    );
    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      const results = await Promise.allSettled(
        selectedAssignments.map((employee) =>
          payrollApi.removePayrollGroupAssignment(employee.employeeId)
        )
      );
      const removedEmployeeIds = results.flatMap((result, index) =>
        result.status === 'fulfilled' ? [selectedAssignments[index].employeeId] : []
      );
      const failureCount = results.length - removedEmployeeIds.length;

      if (removedEmployeeIds.length > 0) {
        await loadOptions();
        setSelected((prev) => {
          const next = { ...prev };
          removedEmployeeIds.forEach((employeeId) => {
            next[employeeId] = false;
          });
          return next;
        });
      }

      if (failureCount === 0) {
        toast.success(t('payroll.removedMany', { count: removedEmployeeIds.length }));
      } else if (removedEmployeeIds.length > 0) {
        toast.error(t('payroll.removePartial', {
          removed: removedEmployeeIds.length,
          failed: failureCount,
        }));
      } else {
        const firstFailure = results.find((result) => result.status === 'rejected');
        const message = firstFailure?.status === 'rejected'
          ? firstFailure.reason?.response?.data?.message
          : null;
        toast.error(message || t('payroll.removeSelectedFailed'));
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>{t('payroll.groupAssignments')}</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('payroll.groupAssignmentsHelp')}
      </Typography>

      {loading || !options ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2}>
          <Paper sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <FormControl fullWidth>
                <InputLabel id="group-label">{t('payroll.payrollGroup')}</InputLabel>
                <Select
                  labelId="group-label"
                  label={t('payroll.payrollGroup')}
                  value={groupId}
                  onChange={(event) => setGroupId(event.target.value)}
                >
                  {options.groups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>{group.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="department-label">{t('employee.department')}</InputLabel>
                <Select
                  labelId="department-label"
                  label={t('employee.department')}
                  value={departmentId}
                  onChange={(event) => setDepartmentId(event.target.value)}
                >
                  <MenuItem value="">{t('employee.allDepartments')}</MenuItem>
                  {options.departments.map((department) => (
                    <MenuItem key={department.id} value={department.id}>{department.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="date"
                label={t('payroll.effectiveFrom')}
                value={effectiveFrom}
                onChange={(event) => setEffectiveFrom(event.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                type="number"
                label={t('payroll.defaultBaseAmount')}
                value={defaultBaseAmount}
                onChange={(event) => setDefaultBaseAmount(event.target.value)}
                fullWidth
              />
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
              mt={2}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedCount > 0 && !allSelected}
                    disabled={selectableEmployees.length === 0}
                    onChange={toggleAll}
                  />
                }
                label={t('payroll.selectAll', {
                  selected: selectedCount,
                  total: selectableEmployees.length,
                })}
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {selectedAssignments.length > 1 && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    disabled={saving}
                    onClick={handleBulkRemoveAssignments}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('payroll.bulkDelete', { count: selectedAssignments.length })}
                  </Button>
                )}
                <Button variant="contained" disabled={saving} onClick={handleApply} sx={{ textTransform: 'none' }}>
                  {saving ? t('common.processing') : t('payroll.applyAndGenerate')}
                </Button>
              </Stack>
            </Stack>
          </Paper>

          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>{t('employee.employee')}</TableCell>
                    <TableCell>{t('common.code')}</TableCell>
                    <TableCell>{t('employee.department')}</TableCell>
                    <TableCell>{t('payroll.baseAmount')}</TableCell>
                    <TableCell>{t('payroll.currentGroup')}</TableCell>
                    <TableCell align="center">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">{t('payroll.noEmployeesForSelection')}</TableCell>
                    </TableRow>
                  ) : (
                    employees.map((employee) => {
                      const checkboxDisabled = !employee.payrollEnabled || !employee.isActive;
                      const disabledTooltip = !employee.payrollEnabled
                        ? t('payroll.noPayrollAccess')
                        : '';

                      return (
                        <TableRow key={employee.employeeId}>
                          <TableCell padding="checkbox">
                            <Tooltip title={disabledTooltip} disableHoverListener={!disabledTooltip}>
                              <span>
                                <Checkbox
                                  checked={!!selected[employee.employeeId]}
                                  disabled={checkboxDisabled}
                                  onChange={(event) => setSelected((prev) => ({ ...prev, [employee.employeeId]: event.target.checked }))}
                                />
                              </span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{employee.employeeName}</TableCell>
                          <TableCell>{employee.employeeCode || '-'}</TableCell>
                          <TableCell>{employee.departmentName || '-'}</TableCell>
                          <TableCell sx={{ width: 180 }}>
                            <TextField
                              size="small"
                              type="number"
                              value={baseAmounts[employee.employeeId] ?? ''}
                              onChange={(event) =>
                                setBaseAmounts((prev) => ({ ...prev, [employee.employeeId]: event.target.value }))
                              }
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            {options.groups.find((group) => group.id === employee.assignedGroupId)?.name || '-'}
                          </TableCell>
                          <TableCell align="center">
                            {employee.assignedGroupId ? (
                              <Tooltip title={t('payroll.removeEmployee')}>
                                <span>
                                  <IconButton
                                    color="error"
                                    size="small"
                                    disabled={saving}
                                    onClick={() => handleRemoveAssignment(employee.employeeId, employee.employeeName)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {result && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" mb={1}>{t('payroll.generationResult')}</Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                {t('payroll.resultSummary', {
                  group: result.groupName,
                  effective: new Date(result.effectiveFrom).toLocaleDateString(),
                  processed: result.processedCount,
                })}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('employee.employee')}</TableCell>
                      <TableCell align="right">{t('payroll.grossMonthly')}</TableCell>
                      <TableCell align="right">{t('payroll.deductions')}</TableCell>
                      <TableCell align="right">{t('payroll.netMonthly')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.items.map((item) => (
                      <TableRow key={item.employeeId}>
                        <TableCell>{item.employeeName}</TableCell>
                        <TableCell align="right">{item.grossMonthly.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">{item.totalDeductions.toLocaleString('en-IN')}</TableCell>
                        <TableCell align="right">{item.netMonthly.toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default PayrollGroupAssignmentsPage;
